import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  ArrowLeft,
  MapPin,
  Clock,
  Send,
  ChevronRight,
  Users,
  Building2,
  Globe,
  Briefcase,
  Calendar,
  Award,
  Phone,
  Mail,
  ExternalLink,
  CheckCircle2,
  CircleDollarSign,
  Heart,
  Info,
  GraduationCap,
  User,
} from 'lucide-react';
import { useJob } from '@/hooks/useJob';
import { Spinner } from '@/components/common';
import { cn } from '@/lib/utils';
import { formatNumber } from '@/utils';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Job } from '@/types';
import { useApplyJob, useMyAppliedJobs } from '@/hooks/useOffer';
import { useAuthStore } from '@/store/authStore';
import { useMyCandidateProfile } from '@/hooks/useCandidate';
import { PERMISSIONS } from '@/routes/guards';
import { NumberInput } from '@/components/ui/numberInput';
import { useToggleSaveListing } from '@/hooks/useSavedListings';

/* ─── helpers ─── */
function fmtSalary(min: string | number | null | undefined, max: string | number | null | undefined, unit: string, timeUnit: string) {
  const nMin = typeof min === 'string' ? parseFloat(min) : (min ?? 0);
  const nMax = typeof max === 'string' ? parseFloat(max) : (max ?? 0);

  if (!nMin && !nMax) return 'Thỏa thuận';

  const fmt = (n: number) => {
    if (!n) return '0';
    if (unit === 'VND' && n >= 1_000_000) {
      const million = n / 1_000_000;
      return million % 1 === 0 ? `${million} triệu` : `${million.toFixed(1)} triệu`;
    }
    return formatNumber(n);
  };

  const timeLabel = timeUnit === 'month' ? 'tháng' : 'năm';

  if (nMin && nMax && nMin === nMax) return `${fmt(nMin)} ${unit}/${timeLabel}`;
  if (nMin && nMax) return `${fmt(nMin)} – ${fmt(nMax)} ${unit}/${timeLabel}`;
  if (nMin) return `Từ ${fmt(nMin)} ${unit}/${timeLabel}`;
  if (nMax) return `Đến ${fmt(nMax)} ${unit}/${timeLabel}`;

  return 'Thỏa thuận';
}

function getDeadlineDays(deadline: string | null) {
  if (!deadline) return null;
  const diff = new Date(deadline).getTime() - Date.now();
  const days = Math.ceil(diff / (1000 * 60 * 60 * 24));
  return days > 0 ? days : 0;
}

/* ─── Apply schema ─── */
const applySchema = z.object({
  expectedSalary: z.preprocess((v) => (v === '' || v === undefined ? undefined : Number(String(v).replace(/\D/g, ''))), z.number().min(1000000, 'Lương tối thiểu 1.000.000 VNĐ')),
  coverLetter: z.string().optional(),
});
type ApplyForm = z.infer<typeof applySchema>;

/* ─── Main Page ─── */
export default function JobDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuthStore();

  const [applyOpen, setApplyOpen] = useState(false);
  const [applied, setApplied] = useState(false);

  const { data: job, isLoading } = useJob(id!);
  const { data: myProfile } = useMyCandidateProfile();
  const { mutate: applyJob, isPending: isApplying } = useApplyJob();
  const { isSaved, toggleSave } = useToggleSaveListing();
  const { data: myAppliedJobs } = useMyAppliedJobs(user?.id || '');

  const saved = job ? isSaved(job.id) : false;
  const isApplied = job && myAppliedJobs ? myAppliedJobs.some((offer: any) => offer.entityId === job.id) : false;

  /* ── Apply form ── */
  const {
    register: regApply,
    handleSubmit: handleApply,
    reset: resetApply,
    formState: { errors: applyErrors },
  } = useForm<ApplyForm>({
    resolver: zodResolver(applySchema),
  });

  const onApplySubmit = (data: ApplyForm) => {
    if (!job || !user) return;

    if (!myProfile?.id) {
      alert('Bạn cần tạo hồ sơ ứng viên trước khi ứng tuyển!');
      return;
    }

    if (!myProfile.resumeUrl && (!data.coverLetter || data.coverLetter.length < 20)) {
      alert('Vui lòng nhập thư xin việc (tối thiểu 20 ký tự) vì bạn chưa có CV trên hệ thống!');
      return;
    }

    applyJob(
      {
        entityType: 'JobOffer',
        entityId: job.id,
        receiverId: job.creator?.id || '',
        senderId: user.id,
        message: data.coverLetter || 'Tôi muốn ứng tuyển vị trí này',
        content: {
          position: job.title,
          expectedSalary: data.expectedSalary,

        },
      },
      {
        onSuccess: () => {
          setApplied(true);
          setApplyOpen(false);
          resetApply();
        },
      },
    );
  };

  if (isLoading) return <Spinner />;
  if (!job)
    return (
      <div className="text-center py-24">
        <p className="text-5xl mb-4">💼</p>
        <p className="font-display font-bold text-xl mb-2" style={{ color: 'var(--text-1)' }}>
          Không tìm thấy việc làm
        </p>
        <Link to="/viec-lam">
          <button className="btn-outline-brand mt-4 px-5 py-2 rounded-xl text-sm font-semibold">← Quay lại</button>
        </Link>
      </div>
    );

  const salary = fmtSalary(job.salaryMin, job.salaryMax, job.currencyUnit, job.timeUnit);
  const deadlineDays = getDeadlineDays(job.deadline);

  return (
    <div className=" min-h-screen pb-12">
      <div className="max-w-7xl mx-auto px-4 animate-fade-in">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm mb-4 text-gray-500">
          <Link to="/viec-lam" className="hover:text-orange-500 transition-colors">Việc làm</Link>
          <ChevronRight size={12} />
          <span className="truncate max-w-xs">{job.title}</span>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* ══ LEFT COLUMN ══ */}
          <div className="lg:col-span-2 space-y-6">

            {/* Hero Card */}
            <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-100">
              <h1 className="text-[22px] font-bold text-gray-900 mb-6 leading-tight">
                {job.title}
                <span className="inline-block ml-2 text-green-500"><CheckCircle2 size={18} className="inline" fill="currentColor" /></span>
              </h1>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <div className="flex items-center gap-3 min-w-[300px]">
                  <div className="w-10 h-10 rounded-full bg-orange-50 flex items-center justify-center text-orange-600 shrink-0">
                    <CircleDollarSign size={22} />
                  </div>
                  <div>
                    <p className="text-[13px] text-gray-400 font-medium ">Thu nhập</p>
                    <p className="text-[15px] font-bold text-gray-800">{salary}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-orange-50 flex items-center justify-center text-orange-600 shrink-0">
                    <MapPin size={22} />
                  </div>
                  <div>
                    <p className="text-[13px] text-gray-400 font-medium">Địa điểm</p>
                    <p className="text-[15px] font-bold text-gray-800">{job.province?.name || 'Toàn quốc'}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-orange-50 flex items-center justify-center text-orange-600 shrink-0">
                    <Award size={22} />
                  </div>
                  <div>
                    <p className="text-[13px] text-gray-400 font-medium">Kinh nghiệm</p>
                    <p className="text-[15px] font-bold text-gray-800">{job.experienceLevel || 'Không yêu cầu'}</p>
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap items-center justify-between gap-4 pt-2 border-t border-gray-50">
                <div className="flex items-center gap-6">
                  {/* <div className="flex items-center gap-2 text-sm text-gray-500 bg-gray-50 px-3 py-1.5 rounded-sm border border-gray-100">
                    <Users size={14} className="text-orange-500" />
                    <span>Xem số người đã ứng tuyển</span>
                    <span className="ml-1 px-1.5 py-0.5 bg-orange-500 text-[10px] text-white font-bold rounded-sm uppercase">New</span>
                  </div> */}
                  <div className="text-[13px] text-gray-500">
                    Hạn nộp hồ sơ: <b className="text-gray-700">{job.deadline ? new Date(job.deadline).toLocaleDateString('vi-VN') : 'Không giới hạn'}</b>
                    {deadlineDays !== null && <span className="ml-1 text-gray-400">({deadlineDays > 0 ? `Còn ${deadlineDays} ngày` : 'Hết hạn'})</span>}
                  </div>
                </div>
              </div>
              {PERMISSIONS.canApplyJob(user?.role) && (
                <div className="flex gap-3 mt-6">
                  <button
                    onClick={() => !isApplied && !applied && setApplyOpen(true)}
                    disabled={isApplied || applied}
                    className={cn(
                      "flex-1 flex items-center justify-center gap-2 py-3.5 rounded-md font-bold transition-all",
                      isApplied || applied
                        ? "bg-gray-100 text-gray-500 cursor-not-allowed border border-gray-200"
                        : "bg-orange-500 hover:bg-orange-600 text-white"
                    )}
                  >
                    <Send size={18} /> {isApplied || applied ? 'Đã ứng tuyển' : 'Ứng tuyển ngay'}
                  </button>
                  <button onClick={() => toggleSave(job.id)} className={cn("px-8 py-3.5 rounded-md font-bold border border-orange-500 text-orange-500 hover:bg-orange-50 transition-all flex items-center justify-center gap-2", saved && "bg-orange-50")}>
                    <Heart size={18} className={cn(saved && "fill-orange-500")} /> {saved ? 'Đã lưu' : 'Lưu tin'}
                  </button>
                </div>
              )}
            </div>

            {/* Chi tiết tin tuyển dụng */}
            <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-100">
              <h2 className="text-lg font-bold text-gray-900 mb-6 border-l-4 border-orange-500 pl-4">
                Chi tiết tin tuyển dụng
              </h2>

              <div className="space-y-4 mb-8">
                <SummaryRow label="Yêu cầu:">
                  <Badge text={job.experienceLevel || 'Chưa cập nhật'} />
                  <Badge text={job.educationRequirement || 'Chưa cập nhật'} />
                </SummaryRow>
                <SummaryRow label="Quyền lợi:">
                  {Array.isArray(job.benefits) && job.benefits.length > 0 ? (
                    job.benefits.slice(0, 3).map((b, i) => <Badge key={i} text={b} />)
                  ) : typeof job.benefits === 'string' && job.benefits.trim() !== '' ? (
                    (job.benefits as any).split('\n').slice(0, 3).map((b: string, i: number) => <Badge key={i} text={b.replace(/^- /, '')} />)
                  ) : (
                    <span className="text-gray-400 text-sm italic">Chưa cập nhật</span>
                  )}
                </SummaryRow>
                <SummaryRow label="Chuyên môn:">
                  <Badge text={job.jobCategoryName || 'Chưa cập nhật'} />
                  {Array.isArray(job.skills) && job.skills.length > 0 ? (
                    job.skills.map((s, i) => <Badge key={i} text={s} />)
                  ) : typeof job.skills === 'string' && job.skills.trim() !== '' ? (
                    (job.skills as any).split(',').map((s: string, i: number) => <Badge key={i} text={s.trim()} />)
                  ) : (
                    <span className="text-gray-400 text-sm italic">Chưa cập nhật</span>
                  )}
                </SummaryRow>
              </div>

              <div className="space-y-8">
                <ContentSection title="Mô tả công việc">
                  <div className="text-[15px] text-gray-700 leading-relaxed whitespace-pre-wrap">
                    {job.description || 'Chưa cập nhật mô tả công việc.'}
                  </div>
                </ContentSection>

                <ContentSection title="Yêu cầu ứng viên">
                  <div className="text-[15px] text-gray-700 leading-relaxed whitespace-pre-wrap">
                    {job.educationRequirement && <p className="mb-2">• {job.educationRequirement}</p>}
                    {(Array.isArray(job.skills) && job.skills.length > 0) || (typeof job.skills === 'string' && job.skills.trim()) ? (
                      <p className="mb-2">
                        • Kỹ năng: {Array.isArray(job.skills) ? job.skills.join(', ') : (job.skills as any)}
                      </p>
                    ) : null}
                    {(!job.educationRequirement && !job.skills?.length) && 'Chưa cập nhật yêu cầu ứng viên.'}
                  </div>
                </ContentSection>

                <ContentSection title="Quyền lợi được hưởng">
                  <div className="text-[15px] text-gray-700 leading-relaxed whitespace-pre-wrap">
                    {Array.isArray(job.benefits) && job.benefits.length > 0
                      ? job.benefits.join('\n')
                      : typeof job.benefits === 'string' && job.benefits.trim()
                        ? (job.benefits as any)
                        : 'Chưa cập nhật quyền lợi.'}
                  </div>
                </ContentSection>

                <ContentSection title="Địa điểm làm việc">
                  <div className="text-[15px] text-gray-700">
                    • {[job.ward?.name, job.district?.name, job.province?.name].filter(Boolean).join(', ') || job.workingLocation || 'Chưa cập nhật địa điểm.'}
                  </div>
                </ContentSection>




                <ContentSection title="Cách thức ứng tuyển">
                  <div className="bg-orange-50/20 rounded-lg p-6 border border-orange-100">
                    <p className="text-[15px] text-gray-700 mb-4">
                      Ứng viên nộp hồ sơ trực tuyến bằng cách bấm <b className="text-orange-600">Ứng tuyển ngay</b> dưới đây.
                    </p>
                    <div className="text-[14px] text-gray-500 mb-6">
                      Hạn nộp hồ sơ: <b className="text-gray-800">{job.deadline ? new Date(job.deadline).toLocaleDateString('vi-VN') : 'Không giới hạn'}</b>
                    </div>
                    {PERMISSIONS.canApplyJob(user?.role) && (
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => !isApplied && !applied && setApplyOpen(true)}
                          disabled={isApplied || applied}
                          className={cn(
                            "px-8 py-3 rounded-md font-bold transition-all shadow-md flex items-center gap-2",
                            isApplied || applied
                              ? "bg-gray-100 text-gray-500 cursor-not-allowed border border-gray-200 shadow-none"
                              : "bg-orange-500 hover:bg-orange-600 text-white"
                          )}
                        >
                          <Send size={18} /> {isApplied || applied ? 'Đã ứng tuyển' : 'Ứng tuyển ngay'}
                        </button>
                        <button onClick={() => toggleSave(job.id)} className="bg-white border border-gray-200 text-gray-600 px-8 py-3 rounded-md font-bold hover:bg-gray-50 transition-all flex items-center gap-2">
                          <Heart size={18} className={cn("text-orange-500", saved && "fill-orange-500")} /> {saved ? 'Đã lưu' : 'Lưu tin'}
                        </button>
                      </div>
                    )}
                  </div>
                </ContentSection>
              </div>
            </div>
          </div>

          {/* ══ RIGHT COLUMN ══ */}
          <div className="space-y-6">

            {/* Company Card */}
            {job.company && (
              <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-100">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-16 h-16 rounded-md border border-gray-100 flex items-center justify-center shrink-0 p-1 bg-white">
                    {job.company.logoUrl ? (
                      <img src={job.company.logoUrl} alt={job.company.name} className="w-full h-full object-contain" />
                    ) : (
                      <Building2 size={32} className="text-gray-200" />
                    )}
                  </div>
                  <h3 className="font-bold text-gray-900 leading-tight">
                    {job.company.name}
                  </h3>
                </div>

                <div className="space-y-4 mb-6">
                  <SidebarItem icon={<Users size={16} />} label="Quy mô:" value={job.company.size || 'Chưa cập nhật'} />
                  <SidebarItem icon={<Briefcase size={16} />} label="Lĩnh vực:" value={job.company.businessSectorName || 'Chưa cập nhật'} />
                  <SidebarItem icon={<MapPin size={16} />} label="Địa điểm:" value={job.company.address || 'Chưa cập nhật'} />
                </div>

                <Link
                  to={`/cong-ty/${job.company.id}`}
                  className="text-orange-600 text-sm font-bold flex items-center justify-center gap-1 hover:underline pt-4 border-t border-gray-50"
                >
                  Xem trang công ty <ExternalLink size={14} />
                </Link>
              </div>
            )}

            {/* Thông tin chung */}
            <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-100">
              <h3 className="text-lg font-bold text-gray-900 mb-6">Thông tin chung</h3>
              <div className="space-y-6">
                <GeneralInfoItem icon={<Award size={18} />} label="Cấp bậc" value={job.jobTitleName || 'Nhân viên'} />
                <GeneralInfoItem icon={<GraduationCap size={18} />} label="Học vấn" value={job.educationRequirement || 'Không yêu cầu'} />
                <GeneralInfoItem icon={<Users size={18} />} label="Số lượng tuyển" value={`${job.vacancies || 0} người`} />
                <GeneralInfoItem icon={<Clock size={18} />} label="Hình thức làm việc" value={job.jobType || 'Toàn thời gian'} />
                <GeneralInfoItem icon={<Calendar size={18} />} label="Thời gian thử việc" value={job.probationMonths ? `${job.probationMonths} tháng` : 'Chưa cập nhật'} />
                <GeneralInfoItem icon={<Clock size={18} />} label="Thời gian làm việc" value={job.workingHours || 'Chưa cập nhật'} />
              </div>
            </div>

            {/* Related Categories (Placeholder as seen in screenshot) */}
            <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-100">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Danh mục Nghề liên quan</h3>
              <div className="flex flex-wrap gap-2">
                <Badge text={job.jobCategoryName} />
                <Badge text={job.jobTitleName} />
                {Array.isArray(job.skills) && job.skills.length > 0 ? (
                  job.skills.map((s, i) => <Badge key={i} text={s} />)
                ) : typeof job.skills === 'string' && job.skills.trim() ? (
                  (job.skills as any).split(',').map((s: string, i: number) => <Badge key={i} text={s.trim()} />)
                ) : null}
              </div>
            </div>

          </div>
        </div>
      </div>

      {/* ══ APPLY MODAL ══ */}
      {applyOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-[2px] animate-in fade-in duration-200">
          <div className="bg-white rounded-lg w-full max-w-xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200">
            <div className="bg-orange-600 px-6 py-4 flex justify-between items-center text-white">
              <h3 className="text-lg font-bold">Ứng tuyển vị trí</h3>
              <button onClick={() => setApplyOpen(false)}><ArrowLeft size={22} /></button>
            </div>
            <div className="p-6">
              <div className="flex items-start gap-4 mb-6 bg-gray-50 p-4 rounded-lg border border-gray-100">
                <Info size={20} className="text-orange-500 shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-bold text-gray-900 mb-1">{job.title}</p>
                  <p className="text-xs text-gray-500">{job.company?.name}</p>
                </div>
              </div>

              {!myProfile?.id ? (
                <div className="text-center py-8">
                  <User size={48} className="mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-600 mb-6">Bạn cần tạo hồ sơ ứng viên trước khi ứng tuyển.</p>
                  <Link to="/ho-so-ung-vien" className="inline-flex items-center gap-2 bg-orange-500 text-white px-6 py-3 rounded-md font-bold">
                    Tạo hồ sơ ngay
                  </Link>
                </div>
              ) : (
                <form onSubmit={handleApply(onApplySubmit)} className="space-y-6">
                  {myProfile.resumeUrl ? (
                    <div className="bg-orange-50/50 border border-orange-100 rounded-lg p-4 mb-4">
                      <p className="text-sm font-bold text-gray-800 mb-2">Thông tin ứng tuyển của bạn:</p>
                      <ul className="text-sm text-gray-600 space-y-1">
                        <li>Họ tên: <b>{myProfile.fullName}</b></li>
                        {myProfile.phoneNumber && <li>SĐT: <b>{myProfile.phoneNumber}</b></li>}
                        {myProfile.desiredPosition && <li>Vị trí: <b>{myProfile.desiredPosition}</b></li>}
                        {myProfile.experience !== undefined && <li>Kinh nghiệm: <b>{myProfile.experience} năm</b></li>}
                        {myProfile.location && <li>Khu vực: <b>{myProfile.location}</b></li>}
                        <li>
                          CV: <a href={myProfile.resumeUrl} target="_blank" rel="noreferrer" className="text-orange-600 hover:underline font-medium">Xem CV đính kèm</a>
                        </li>
                      </ul>
                    </div>
                  ) : (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4 text-sm text-yellow-800">
                      Bạn chưa cập nhật Link CV. Vui lòng nhập <b>Thư xin việc</b> để nhà tuyển dụng hiểu thêm về bạn.
                    </div>
                  )}

                  <NumberInput label="Mức lương mong muốn (VNĐ)" placeholder="Ví dụ: 10.000.000" error={applyErrors.expectedSalary?.message} {...regApply('expectedSalary')} />

                  {(!myProfile.resumeUrl || true) && (
                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-2">Thư giới thiệu {!myProfile.resumeUrl && <span className="text-red-500">*</span>}</label>
                      <textarea rows={5} placeholder="Giới thiệu bản thân..." className={cn('w-full rounded-md border px-4 py-3 text-sm outline-none focus:border-orange-500 transition-all', applyErrors.coverLetter ? 'border-red-400' : 'border-gray-200')} {...regApply('coverLetter')} />
                      {applyErrors.coverLetter && <p className="text-xs text-red-500 mt-1">{applyErrors.coverLetter.message}</p>}
                    </div>
                  )}

                  <div className="flex gap-3 pt-2 border-t mt-6">
                    <button type="submit" disabled={isApplying} className="flex-1 bg-orange-500 hover:bg-orange-600 text-white py-3 rounded-md font-bold transition-all disabled:opacity-50">
                      {isApplying ? 'Đang gửi...' : myProfile.resumeUrl ? 'Xác nhận ứng tuyển' : 'Nộp hồ sơ ngay'}
                    </button>
                    <button type="button" onClick={() => setApplyOpen(false)} className="px-6 py-3 border border-gray-200 text-gray-700 hover:bg-gray-50 rounded-md font-bold">Hủy</button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      )}

    </div>
  );
}


function SummaryRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-start gap-3">
      <span className="text-sm text-gray-500 shrink-0 mt-1 font-medium min-w-[80px]">{label}</span>
      <div className="flex flex-wrap gap-2">{children}</div>
    </div>
  );
}

function Badge({ text }: { text: string | null | undefined }) {
  if (!text) return null;
  return (
    <span className="px-2.5 py-1 bg-gray-100 text-gray-600 text-xs font-medium rounded-sm border border-gray-100">
      {text}
    </span>
  );
}

function ContentSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h4 className="font-bold text-gray-900 mb-3 text-base">{title}</h4>
      <div className="pl-1">{children}</div>
    </div>
  );
}

function SidebarItem({ icon, label, value }: { icon: React.ReactNode; label: string; value: string | null | undefined }) {
  return (
    <div className="flex items-start gap-3">
      <span className="text-gray-400 mt-0.5 shrink-0">{icon}</span>
      <div className="text-sm">
        <span className="text-gray-500 mr-1">{label}</span>
        <span className="text-gray-800 font-medium">{value}</span>
      </div>
    </div>
  );
}

function GeneralInfoItem({ icon, label, value }: { icon: React.ReactNode; label: string; value: string | null | undefined }) {
  return (
    <div className="flex items-center gap-4">
      <div className="w-10 h-10 rounded-full bg-orange-50 flex items-center justify-center text-orange-600 shrink-0">
        {icon}
      </div>
      <div>
        <p className="text-[12px] text-gray-400 font-medium leading-none mb-1">{label}</p>
        <p className="text-[14px] font-bold text-gray-800 leading-tight">{value || 'Chưa cập nhật'}</p>
      </div>
    </div>
  );
}
